import * as vars from './_variables.scss'

// Design system colors (to be adopted in phase 2)
// Reference: https://www.figma.com/design/dfkn35g4dBznG0wFBkhAvC/Cobalt-Design-System?node-id=732-2404&p=f&t=FHyI0RRuk5Wegr5D-0
// Color palette: https://www.figma.com/design/dfkn35g4dBznG0wFBkhAvC/Cobalt-Design-System?node-id=431-466&p=f&t=4Jf1YTr2EU2vR28t-0
// Currently using legacy colors from _variables.scss; will migrate to design system colors as components are updated
export const black = '#000000'
export const blackAlpha95 = 'rgba(0, 0, 0, 0.95)'
export const blackAlpha85 = 'rgba(0, 0, 0, 0.85)'
export const blackAlpha75 = 'rgba(0, 0, 0, 0.75)'
export const blackAlpha65 = 'rgba(0, 0, 0, 0.65)'
export const blackAlpha50 = 'rgba(0, 0, 0, 0.5)'
export const blackAlpha40 = 'rgba(0, 0, 0, 0.4)'
export const blackAlpha30 = 'rgba(0, 0, 0, 0.3)'
export const blackAlpha25 = 'rgba(0, 0, 0, 0.25)'
export const blackAlpha15 = 'rgba(0, 0, 0, 0.15)'
export const blackAlpha12 = 'rgba(0, 0, 0, 0.12)'
export const blackAlpha10 = 'rgba(0, 0, 0, 0.1)'
export const blurple50 = '#F8F9FE'
export const blurple100 = '#F0F2FF'
export const blurple200 = '#DDDFFD'
export const blurple300 = '#A6ACFA'
export const blurple400 = '#848BF8'
export const blurple500 = '#5561F6'
export const blurple600 = '#404BF5'
export const blurple700 = '#202FF3'
export const blurple800 = '#183BB8'
export const blurple900 = '#052595'
export const chartReferenceLine = '#E7E7E7'
export const colorBorderTableRow = '#EDEDED'
export const coolGrey50 = '#FAFBFC'
export const white = '#FFFFFF'

const PALETTE = {
    // Colors from the updated design system (In Progress, can be ignored for now, as we will use them in phase 2)
    black,
    blackAlpha95,
    blackAlpha85,
    blackAlpha75,
    blackAlpha65,
    blackAlpha50,
    blackAlpha40,
    blackAlpha30,
    blackAlpha25,
    blackAlpha15,
    blackAlpha12,
    blackAlpha10,
    chartReferenceLine,
    colorBorderMain: '#D9D9D9',
    colorBorderSecondary: '#05050510',
    colorBorderContainer: blackAlpha12,
    colorBorderTableRow,
    coolGrey50,
    greyWarm: '#CECACA',
    primary50: blurple50,
    primary100: blurple100,
    primary200: blurple200,
    primary300: blurple300,
    primary400: blurple400,
    primary: blurple500,
    primary600: blurple600,
    primary700: blurple700,
    primary800: blurple800,
    primary900: blurple900,
    red50: '#FFF1F0',
    white,
} as const

const TEXT_COLORS = {
    textMainHeading: blackAlpha95,
    textPrimary: blackAlpha85,
    textBase: blackAlpha75,
    textMuted: blackAlpha65,
    textLight: blackAlpha50,
    textLighter: blackAlpha40,
    textExtraLight: blackAlpha30,
    textDisabled: vars.colorTextDisabled,
} as const

export type ThemeTextColor = keyof typeof TEXT_COLORS

export const colors = {
    ...PALETTE,
    ...TEXT_COLORS,
    // Primary
    colorPrimary: vars.colorPrimary,
    colorPrimaryHover: vars.colorPrimaryHover,
    primaryLightened: vars.colorPrimaryLightened,
    // Background Colors:
    colorBackgroundMain: coolGrey50,
    colorBackgroundTabs: coolGrey50,
    colorBackgroundSidebar: vars.colorBackgroundSidebar,
    colorBackgroundSidebarHover: vars.colorBackgroundSidebarHover,
    // Table
    colorTableRowBorder: colorBorderTableRow,
    colorTableHeadBg: vars.colorTableHeadBg,
    colorTableHeadBgHover: vars.colorTableHeadBgHover,
    colorTableHeadSubtitle: vars.colorTableHeadSubtitle,
    colorTableTotalRowBg: vars.colorTableTotalRowBg,
    colorTableTextBg: vars.colorTableTextBg,
    // Deltas
    colorDeltaRed: vars.colorDeltaRed,
    colorDeltaGreen: vars.colorDeltaGreen,
    colorDeltaGray: vars.colorDeltaGray,
    colorDeltaRedBg: vars.colorDeltaRedBg,
    colorDeltaGreenBg: vars.colorDeltaGreenBg,
    colorDeltaGrayBg: vars.colorDeltaGrayBg,
    colorDeltaCyanBlueBg: vars.colorDeltaCyanBlueBg,
    // Resource Header
    colorDetailsFieldText: vars.colorDetailsFieldText,
    colorDetailsTagBg: vars.colorDetailsTagBg,
    colorDetailsIcon: vars.colorDetailsIcon,
    // Rulebooks
    changeSetManuallyChangesProposed: vars.changeSetManuallyChangesProposed,
    changeSetCompletedColor: vars.changeSetCompletedColor,
    changeSetScheduledChangesProposed: vars.changeSetScheduledChangesProposed,
    // Other
    actionButtonColorDark: vars.actionButtonColorDark,
    actionButtonColorLight: vars.actionButtonColorLight,
    actionButtonColorMedium: vars.actionButtonColorMedium,
    cardBorderColor: vars.cardBorderColor,
    chipBackground: vars.chipBackground,
    colorMint: vars.colorMint,
    colorMintGradient: vars.colorMintGradient,
    colorMintLight: vars.colorMintLight,
    dangerColor: vars.dangerColor,
    errorBackColor: vars.errorBackcolor,
    errorColor: vars.errorColor,
    ghostWhite: vars.ghostWhite,
    grayBackgroundColor: vars.grayBackgroundColor,
    linkColor: vars.linkColor,
    linkColorMuted: vars.linkColorMuted,
    popoverHeaderColor: vars.popoverHeaderColor,
    popoverMaskColor: vars.popoverMaskColor,
    sideMenuScrollbarThumbColor: vars.sideMenuScrollbarThumbColor,
    sideMenuItemColor: vars.sideMenuItemColor,
    sideMenuSubmenuItemColor: vars.sideMenuSubmenuItemColor,
    sideMenuScrollbarShadowColor: vars.colorTextExtraLight,
    sideMenuIconColor: vars.sideMenuIconColor,
    sideMenuIconSelectedColor: vars.sideMenuIconSelectedColor,
    successBackColor: vars.successBackcolor,
    successColor: vars.successColor,
    warningBackColor: vars.warningBackcolor,
    warningColor: vars.warningColor,
    whiteDividerColor: vars.whiteDividerColor,
    // Accent Colors
    colorAccentSteelBlue: vars.colorAccentSteelBlue,
    colorAccentAmber: vars.colorAccentAmber,
    colorAccentPurple: vars.colorAccentPurple,
    colorAccentTeal: vars.colorAccentTeal,
    colorAccentTerracotta: vars.colorAccentTerracotta,
    colorAccentCornflower: vars.colorAccentCornflower,
    colorAccentBrown: vars.colorAccentBrown,
    colorAccentOlive: vars.colorAccentOlive,
    colorAccentBerry: vars.colorAccentBerry,
    colorAccentCyan: vars.colorAccentCyan,
}

export type Colors = typeof colors
